import { FC, JSX, useContext, useEffect, useState } from "react";
import { toast, TypeOptions } from "react-toastify";
import moment from "moment";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { ColumnConfig, wafflesKeyColumnName, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderSetSimpleModel } from "@app/provider-sets";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { UserContext } from "@app/user/UserContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";
import ProviderSetService from "@app/provider-sets/providerSetService";

interface BulkActionsPanelProps {
  loading: boolean;
  onActivate: (onClose: () => void, versionIds: number[]) => void;
  onArchive: (onClose: () => void, versionIds: number[]) => void;
  onClose?: () => void; // optionally passed from SidePanel
  onCreateIndexes: (onClose: () => void, versionIds: number[]) => void;
}

enum Actions {
  Activate = "Activate",
  Archive = "Archive",
  CreateIndexes = "Create Indexes",
  RunCCIIODataValidations = "Run CCIIO Data Validations"
}

const columnConfig: ColumnConfig = {
  lastActivationDateDisp: {
    displayText: "Last Activated Date",
    sortOrder: 10,
    testId: "lastActivatedDate",
  },
  lastActivationDateVal: {
    displayText: "Last Activated Date",
    hidden: true,
    sortOrder: 20,
    testId: "lastActivatedDate",
  },
  name: {
    displayText: "Name",
    sortOrder: 1,
    testId: "name",
  }
};

interface ProviderSetTableData {
  [wafflesKeyColumnName]: string;
  name: string;
  lastActivationDateDisp: string;
  lastActivationDateVal: Date
}

const BulkActionsPanelContent: FC<BulkActionsPanelProps> = (props: BulkActionsPanelProps): JSX.Element => {
  const flyoutContext = useContext(FlyoutContext);
  const user = useContext(UserContext);
  const providerSetService = new ProviderSetService();

  const [selectedAction, setSelectedAction] = useState<Actions>();
  const [submitButtonName, setSubmitButtonName] = useState("");
  const [providerSets, setProviderSets] = useState([]);
  const [visibleProviderSets, setVisibleProviderSets] = useState([]);
  const [selectedProviderSetIds, setSelectedProviderSetIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerSetSelectToolTip, setProviderSetSelectToolTip] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const actions: Actions[] = [Actions.Activate, Actions.Archive, Actions.CreateIndexes, Actions.RunCCIIODataValidations];
  const createIndexesTT = "Indexes will not be created for the selected provider network(s) right away. Instead, one or more jobs will be kicked off to handle index creation.";
  const activateTT = "Activating the selected provider network(s) will also activate any associated filtered provider networks.";
  const archiveTT = "The selected provider network(s) will not be archived right away. Instead, a job will be kicked off to handle validation and archiving. Any network that doesn't pass validation will be skipped.";
  const runCCIIODataValidationsTTT = "CCIIO Data Validation will be ran on all selected provider networks.";

  useEffect(() => {
    switch (selectedAction) {
      case Actions.Activate:
        handleActionChange(Actions.Activate, providerSetService.getInactiveNonFilteredProviderSetsAsync(), activateTT);
        break;
      case Actions.Archive:
        handleActionChange(Actions.Archive, providerSetService.getNonArchivedProviderSetsAsync(), archiveTT);
        break;
      case Actions.CreateIndexes:
        handleActionChange(Actions.CreateIndexes, providerSetService.getNonIndexedProviderSets(), createIndexesTT);
        break;
      case Actions.RunCCIIODataValidations:
        handleActionChange(Actions.RunCCIIODataValidations, providerSetService.getCCIIODataValidationEligibleProviderSets(), runCCIIODataValidationsTTT);
        break;
      default:
        setProviderSets([]);
        setSelectedProviderSetIds([]);
    }
    setNameFilter("");
  }, [selectedAction]);

  useEffect(() => {
    setVisibleProviderSets(providerSets.filter(x => x.name.toLowerCase().includes(nameFilter.toLowerCase())));
  }, [providerSets, nameFilter])

  const handleSubmit = async () => {
    let submitFunc = null;
    switch (selectedAction) {
      case Actions.Activate:
        submitFunc = providerSetService.activateLatestProviderSetVersionAsync(selectedProviderSetIds);
        break;
      case Actions.Archive:
        submitFunc = providerSetService.archiveProviderSetsAsync(selectedProviderSetIds);
        break;
      case Actions.CreateIndexes:
        submitFunc = providerSetService.createLatestProviderSetVersionIndexesAsync(selectedProviderSetIds);
        break;
      case Actions.RunCCIIODataValidations:
        submitFunc = providerSetService.runCCIIODataValidations(selectedProviderSetIds);
        break;
    }

    setLoading(true);
    submitFunc
      .then((response) => {
        if (response.length === selectedProviderSetIds.length) {
          handleToast("Operation complete.", "success");
        } else {
          handleToast("Some provider networks were not processed successfully.", "warning");
        }
        flyoutContext.handleCloseFlyout();
        setLoading(false);
      }, () => {
        handleToast("An error occurred while processing your provider networks.", "error");
        flyoutContext.handleCloseFlyout();
        setLoading(false);
      });
  }

  const handleToast = (message: string, type: TypeOptions) => {
    toast(message,
      {
        containerId: "z-index-9999",
        type: type
      }
    );
  }

  const getActions = (): Actions[] => {
    let allowedActions = [...actions];
    if (!user.roles.includes(Roles.PROVIDER_VALIDATION_USER)) {
      allowedActions = actions.filter(x => x !== Actions.RunCCIIODataValidations);
    } 
    return allowedActions;
  }

  const handleCheckboxChange = (keys: string[]) => {
    const temp = visibleProviderSets.some(vps => vps.providerSetId === selectedProviderSetIds[0]);
    console.log(temp);
    const newSelections = selectedProviderSetIds.filter(sps => !visibleProviderSets.some(vps => vps.providerSetId.toString() === sps));
    setSelectedProviderSetIds(newSelections.concat(keys));
  }

  const handleActionChange = (subBtnName: string, providersFunc: Promise<ProviderSetSimpleModel[]>, providerSetsToolTipText: string) => {
    setLoading(true);
    setSubmitButtonName(subBtnName);
    setProviderSetSelectToolTip(providerSetsToolTipText);
    providersFunc
      .then((response) => {
        setProviderSets(response);
        setSelectedProviderSetIds([]);
        setLoading(false);
      }, () => {
        handleToast("An error occurred while retrieving your provider networks.", "error");
        setLoading(false);
      });
  }

  const buildProviderSetTableData = (providerSets: ProviderSetSimpleModel[]): ProviderSetTableData[] => {
    const tableData: ProviderSetTableData[] = providerSets.map((ps: ProviderSetSimpleModel) => {
      return {
        key: ps.providerSetId.toString(),
        lastActivationDateDisp: !!ps.lastActivationDate ? moment.utc(ps.lastActivationDate).local().format("MMM DD, YYYY h:mm:ss A") : "(none)",
        lastActivationDateVal: ps.lastActivationDate,
        name: ps.name
      };
    });

    return tableData;
  }

  const handleSort = (data: ProviderSetTableData[], key: keyof ProviderSetTableData, sortDirection: SortDirections) => {
    if (sortDirection === SortDirections.None)
      return data;

    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (key) {
      case "name":
        return data.sort((a, b) => (a[key] > b[key] ? 1 : -1) * multiplier);
      case "lastActivationDateDisp":
        return data.sort((a, b) => (a.lastActivationDateVal > b.lastActivationDateVal ? 1 : -1) * multiplier);
    }
    return data;
  }

  return (
    <>
      <QESSelect
        testId="provider-set-bulk-actions-panel-content__action"
        containerClassName="col-4 mb-2"
        label="Actions"
        allowEmpty={true}
        value={selectedAction}
        placeholder={"Select an Action"}
        disabled={props.loading || loading}
        onChange={(e) => {
          setSelectedAction(e.target.value as Actions);
        }}
      >
        {getActions().map((a) => <option key={a} value={a}>{a}</option>)}
      </QESSelect>
      {!selectedAction ? <></> :
        <>
          <QESTextInput containerClassName="ps-0 mb-2 col-3" label="Name" testId="provider-set-bulk-actions-filter__name" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} />
          <div className="d-flex align-items-center">
          Provider Networks
            <TooltipWrapper
              containerClassName="pb-1"
              text={providerSetSelectToolTip}
              tooltipWidth="350px"
            >
              <span className="ms-2"><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="18"/></span>
            </TooltipWrapper>
          </div>
          <Waffles<ProviderSetTableData>
            columns={columnConfig}
            data={buildProviderSetTableData(visibleProviderSets)}
            enablePagination={true}
            fullWidth={true}
            loading={loading}
            testId="provider-set-bulk-actions-waffles-table"
            selectedKeys={selectedProviderSetIds}
            showCheckboxes={true}
            handleCheckboxChange={handleCheckboxChange}
            customSort={handleSort}
          />
        </>
      }
      <FlyoutFooter
        submitButton={
          <QESButton qesTestId="provider-set-bulk-actions-panel-content__submit"
            hidden={!selectedAction}
            disabled={selectedProviderSetIds.length == 0 || loading}
            onClick={() => {
              handleSubmit();
            }}>
            {submitButtonName}
          </QESButton>
        }
      />
    </>
  );
}

export default BulkActionsPanelContent;
